import React, { useEffect, useState } from "react";
import ReactApexChart from "react-apexcharts";
import Skeleton from "@mui/material/Skeleton";
import { Grid as GridLoading } from "react-loader-spinner";

const Treemap = ({
  onSelectCustomerIntent,
  isLoading,
  treeData,
  mode,
  startDate,
  endDate,
}) => {
  const [clickedIndex, setClickedIndex] = useState(null);
  const [series, setSeries] = useState([]);
  const [colorRanges, setColorRanges] = useState([]);
  const [options, setOptions] = useState({});
  const [rootCauses, setRootCauses] = useState([]);
  const [rootCausesLoading, setRootCausesLoading] = useState(false);

  useEffect(() => {
    if (treeData && treeData.length > 0) {
      const data = treeData[0].data;
      const totalY = data.reduce((acc, item) => acc + item.y, 0);
      const minValue = Math.min(...data.map((item) => item.y));
      const maxValue = Math.max(...data.map((item) => item.y));

      const newColorRanges = [
        { from: minValue, to: (minValue + maxValue) / 3, color: "#E0E0E0" },
        {
          from: (minValue + maxValue) / 3,
          to: (2 * (minValue + maxValue)) / 3,
          color: "#A0A0A0",
        },
        {
          from: (2 * (minValue + maxValue)) / 3,
          to: maxValue,
          color: "#404040",
        },
      ];

      setColorRanges(newColorRanges);
      setSeries(treeData);

      setOptions({
        chart: {
          type: "treemap",
          toolbar: { show: true },
          events: {
            dataPointSelection: (event, chartContext, config) => {
              const dataIndex = config.dataPointIndex;
              if (dataIndex !== -1) {
                clickedIndex === null &&
                  onSelectCustomerIntent(data[dataIndex].x);
                setClickedIndex((prevIndex) =>
                  prevIndex === dataIndex ? null : dataIndex
                );

                fetchRootCauses(data[dataIndex].x);
              }
            },
          },
        },
        title: {
          text: `Customer Intents`,
          align: "center",
          style: { fontSize: "18px", fontWeight: "bold" },
        },
        tooltip: {
          y: {
            formatter: (val, { seriesIndex, dataPointIndex }) => {
              const percentage =
                ((data[dataPointIndex]?.y || 0) / totalY) * 100;
              return ` ${percentage.toFixed(2)}%`;
            },
          },
        },
        plotOptions: {
          treemap: { colorScale: { ranges: newColorRanges } },
        },
        dataLabels: {
          enabled: true,
          style: { colors: ["black"], fontSize: "20px" },
          formatter: (val, { seriesIndex, dataPointIndex }) => {
            const percentage = ((data[dataPointIndex]?.y || 0) / totalY) * 100;
            return `${data[dataPointIndex]?.x} : ${percentage.toFixed(2)} %`;
          },
        },
        states: {
          active: {
            allowMultipleDataPointsSelection: false,
            filter: { type: "none" },
          },
          hover: { filter: { type: "none" } },
        },
      });
    }
  }, [treeData, onSelectCustomerIntent]);

  const fetchRootCauses = async (intent) => {
    setRootCausesLoading(true);
    try {
      const formattedStartDate = startDate.toISOString().split("T")[0];
      const formattedEndDate = endDate.toISOString().split("T")[0];

      const rootCausesResponse = await fetch(
        `/l4_root_causes?start_date=${formattedStartDate}&end_date=${formattedEndDate}&l3_category=${intent}`
      );
      const rootCausesData = await rootCausesResponse.json();

      setRootCauses(rootCausesData);
      setRootCausesLoading(false);
    } catch (error) {
      setRootCausesLoading(false);
      console.error("Error fetching root causes:", error);
    }
  };

  return (
    <>
      {isLoading || !treeData || treeData.length === 0 ? (
        <div style={{ height: "100%" }}>
          <h3 style={{ width: "100%", textAlign: "center" }}>
            Customer Intents
          </h3>
          <div className='search-loading-area' style={{ height: "80%" }}>
            {!isLoading ? (
              <div
                style={{
                  width: "100%",
                  height: "100%",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                No Data Found
              </div>
            ) : (
              <>
                <Skeleton width='100%' height='100%' />
                <span className='tree-map-centered-text'>
                  <GridLoading
                    visible={true}
                    height='15'
                    width='15'
                    color={mode === "dark" ? "white" : "black"}
                    ariaLabel='grid-loading'
                    radius='12.5'
                  />
                  Retrieving data...
                </span>
              </>
            )}
          </div>
        </div>
      ) : (
        <div
          id='treemap-chart'
          className='complaints-treemap-chart'
          style={{ height: "100%", overflow: "hidden", position: "relative" }}
        >
          <ReactApexChart
            options={options}
            series={series}
            type='treemap'
            height='100%'
          />

          {/* Modal */}
          {clickedIndex !== null && (
            <div className='modal'>
              <div className='modal-content'>
                <button className='close' onClick={() => setClickedIndex(null)}>
                  &times;
                </button>
                <h2>{treeData[0]?.data[clickedIndex].x}</h2>
                {rootCausesLoading ? (
                  <Skeleton width='100%' height='100%' />
                ) : (
                  <div>
                    {rootCauses.map((cause, index) => (
                      <div key={index}>
                        <h4>
                          {cause.root_cause} ({cause.count})
                        </h4>
                        <ul>
                          {cause.snippets.map((snippet, snippetIndex) => (
                            <li key={snippetIndex}>{snippet}</li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </>
  );
};

export default Treemap;
